import React, { useEffect } from "react";

import { Box, Button, Typography, Chip } from "@mui/material";
import { CheckCircleOutline, ErrorOutline, HourglassBottomOutlined } from "@mui/icons-material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { green, red, orange } from "@mui/material/colors";
import { trackEvent } from "../../../utils/analytics";
import { trackMetaEvent } from "../../../utils/metaPixel";
import { trackTikTokEvent } from "../../../utils/tiktokPixel";
import { trackChatGptEvent } from "../../../utils/chatGptPixel";
import { wasPurchaseAlreadyTracked } from "../../../utils/purchaseDedupe";
import { removeCouponCookie } from "../../../utils/affiliateCoupon";
import { useAppSelector } from "../../../store";
import { getFullState } from "../../../store/slices/auth/auth.slice";

type PaymentStatus = "success" | "pending" | "timeout" | "failed";

const getStatus = (success: string | null, pending: string | null): PaymentStatus => {
	if (success === "true") return "success";
	if (pending === "true") return "pending";
	return "failed";
};

export const STATUS_CONFIG: Record<
	PaymentStatus,
	{ icon: React.ReactNode; color: string; title: string; subtitle: string }
> = {
	success: {
		icon: <CheckCircleOutline sx={{ fontSize: 100, color: green[500] }} />,
		color: green[500],
		title: "Payment Successful!",
		subtitle: "Your payment has been processed successfully. Your invitation is now active."
	},
	pending: {
		icon: <HourglassBottomOutlined sx={{ fontSize: 100, color: orange[500] }} />,
		color: orange[500],
		title: "Payment Pending",
		subtitle: "Your payment is being processed."
	},
	timeout: {
		icon: <ErrorOutline sx={{ fontSize: 100, color: red[500] }} />,
		color: red[500],
		title: "Payment Timeout",
		subtitle:
			"This is taking longer than usual. If your payment went through, it'll be reflected shortly — contact support if it isn't within a few minutes."
	},
	failed: {
		icon: <ErrorOutline sx={{ fontSize: 100, color: red[500] }} />,
		color: red[500],
		title: "Payment Failed",
		subtitle: "Your payment could not be completed. Please try again or contact support."
	}
};

const PaymentCallback = () => {
	const params = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { user } = useAppSelector(getFullState);
	const success = searchParams.get("success");
	const pending = searchParams.get("pending");
	const txnResponseCode = searchParams.get("txn_response_code");
	const message = searchParams.get("message");
	const transactionId = searchParams.get("id");
	const amountCents = searchParams.get("amount_cents");

	const status = getStatus(success, pending);
	const config = STATUS_CONFIG[status];

	useEffect(() => {
		if (wasPurchaseAlreadyTracked(transactionId)) return;
		trackEvent("pay_wedding_invitation", {
			scope: "Invitations",
			description: "Pay Wedding Invitation"
		});
		trackMetaEvent(
			"pay_wedding_invitation",
			{ invitation_id: params.id, currency: "EGP", value: Number(amountCents) / 100 },
			true
		);
		trackTikTokEvent("Purchase", {
			content_id: params.id,
			content_type: "product",
			currency: "EGP",
			value: Number(amountCents) / 100
		});
		trackChatGptEvent("Purchase", { currency: "EGP", value: Number(amountCents) / 100 });
		// remove coupon cookie
		if (user) {
			removeCouponCookie(user._id);
		}
	}, [user, params.id, transactionId]);

	return (
		<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="80vh" px={2}>
			{config.icon}

			<Typography variant="h4" fontWeight="bold" mt={3} textAlign="center" fontFamily="var(--openSans), sans-serif">
				{config.title}
			</Typography>

			<Typography variant="subtitle1" textAlign="center" mt={1} color="text.secondary" maxWidth={440}>
				{config.subtitle}
			</Typography>

			{message && status === "failed" && <Chip label={message} variant="outlined" color="error" sx={{ mt: 2 }} />}

			{txnResponseCode && txnResponseCode !== "APPROVED" && (
				<Typography variant="caption" color="text.disabled" mt={1}>
					Code: {txnResponseCode}
				</Typography>
			)}

			{transactionId && (
				<Typography variant="caption" color="text.disabled" mt={0.5}>
					Transaction ID: {transactionId}
				</Typography>
			)}

			{status !== "pending" && (
				<Button
					variant="contained"
					size="large"
					sx={{ mt: 4, px: 5 }}
					onClick={() => navigate(`/manageWeddingInvitation/${params.id}`)}
				>
					Back to Invitation
				</Button>
			)}
		</Box>
	);
};

export default PaymentCallback;
